import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import List from 'modules/Finance/PaymentProfiles/subcomponents/List'
import FilterPanel from 'components/Filters/FilterPanel'
import config from 'modules/Finance/PaymentProfiles/config/filters'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import PageHeader from 'components/PageHeader'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SET_PAYMENT_PROFILES_FILTER } from 'modules/Finance/PaymentProfiles/store/types'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import GridSpinner from 'components/UI/GridSpinner'
import EmptyResults from 'components/EmptyResults'
import { Button, Icon } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { usePaymentProfiles } from 'modules/Finance/PaymentProfiles/hooks/usePaymentProfiles'

const PaymentProfiles = () => {
  const [page, setPage] = useState(1)
  const { t } = useTranslation()
  const { md } = useBreakpoint()
  const pathGen = useGeneratePathI18n()
  const { getPaymentProfiles, resetPaymentProfiles, dropPaymentProfiles } = usePaymentProfiles()

  const { paymentProfiles: { data, loading, pagination = { limit: 50 }, loaded } = {}, filters } =
    useSelector((state) => state['finance/payment-profiles'])

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: SET_PAYMENT_PROFILES_FILTER,
    localStorageKey: 'payment-profiles',
  })

  useEffect(() => {
    filterManager.params &&
      getPaymentProfiles({
        ...filterManager.params,
        offset: (page - 1) * pagination.limit,
        limit: pagination.limit,
      })
  }, [filterManager.params, page])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(filterManager.params, data, loaded)

  useEffect(() => {
    dropPaymentProfiles()
    setPage(1)
  }, [filterManager.params])

  useEffect(() => {
    return () => {
      resetPaymentProfiles()
    }
  }, [])

  return (
    <>
      {firstLoad ? (
        <GridSpinner width={32} />
      ) : totallyEmptyList ? (
        <div className={'layout-tile'}>
          <EmptyResults
            icon={
              <Icon
                name={'users'}
                color={'Interface-Onsurface'}
              />
            }
            header={t('payment_profiles::empty_list.heading')}
            text={t('payment_profiles::empty_list.text')}
            actionButton={
              <Button
                startIcon={<Icon name={'plus'} />}
                variant={'primary'}
                component={Link}
                to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW)}
              >
                {t('common::buttons.create_profile')}
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className={'layout-tile margin-bottom-16'}>
            <PageHeader className={'margin-bottom-24'}>{t('payment_profiles::header')}</PageHeader>
            <Button
              startIcon={<Icon name={'plus'} />}
              variant={'primary'}
              fullWidth={!md}
              component={Link}
              to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW)}
              size={!md ? 'm' : 'l'}
            >
              {t('common::buttons.create_profile')}
            </Button>
          </div>

          <div className={'layout-tile'}>
            <FilterPanel
              filterManager={filterManager}
              className={'margin-bottom-32'}
            />
            <List
              EmptyComponent={() => (
                <EmptyResults
                  icon={
                    <Icon
                      name={'users'}
                      color={'Interface-Onsurface'}
                    />
                  }
                />
              )}
              loading={loading}
              data={data}
              pagination={pagination}
              onChangePage={(val) => setPage(page + val)}
            />
          </div>
        </>
      )}
    </>
  )
}

export default PaymentProfiles
